import { computed, ref } from "vue";

import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import { defineStore } from "pinia";

import { windowWithEcho } from "@/helpers/websockets";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type {
  AccessTokenResponse,
  ActiveCallInfo,
  AnswerCallResponse,
  CallState,
  CallStateChangedEvent,
  DialerActivationEvent,
  DialerConnectionState,
  DialerType,
  GetCallStateResponse,
  RefreshCallStateResponse,
} from "@/services/dialer-api/types";
import type { Channel } from "laravel-echo/dist/channel";

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const REGISTRATION_HEARTBEAT_MS = 60 * 1000; // 1 minute (API TTL is 3 min)

/**
 * Dialer API Store.
 *
 * Browser tabs register as "online" so the API knows when a user has any
 * dialer available to receive calls. Without this, borrowers calling an
 * offline user would wait in queue indefinitely.
 *
 * - Call state via WebSocket (users.{userId} channel)
 * - Focus-based state sync
 * - Heartbeat mechanism for long calls to refresh TTL
 * - Browser online registration with heartbeat + unload safety
 */
export const useDialerApiStore = defineStore("dialerApi", () => {
  // ============================================
  // State
  // ============================================

  const connectionState = ref<DialerConnectionState>("disconnected");
  const callState = ref<CallState | null>(null);
  const isActiveDialer = ref(true); // Default: active (browser is active when alone)
  const isRegisteredAsDialer = ref(false);
  const twilioAccessToken = ref<string | null>(null);

  // Internal (not exposed)
  let userId: number | null = null;
  let usersChannel: Channel | null = null;
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let registrationHeartbeatInterval: ReturnType<typeof setInterval> | null =
    null;
  let boundUnloadHandler: (() => void) | null = null;

  // ============================================
  // Getters
  // ============================================

  const activeCall = computed<ActiveCallInfo | null>(() => {
    if (!callState.value) {
      return null;
    }

    const call = callState.value;
    return {
      sessionId: call.call_session_id?.toString() || "",
      to: call.call_data.to || "",
      from: call.call_data.from || "",
      calleeName: call.call_data.caller_name,
      businessName: call.call_data.caller_business_name,
      partnerName: call.call_data.caller_integration_partner,
      brandName: call.call_data.caller_brand_name,
      applicationId: call.call_data.application_id,
      direction:
        call.call_data.direction === "inbound" ? "incoming" : "outgoing",
      startTime: call.started_at ? call.started_at * 1000 : undefined,
      userPhoneNumberLocation: call.call_data.user_phone_number_location,
    };
  });

  const isConnected = computed(() => connectionState.value === "connected");

  const isOnCall = computed(() => callState.value?.status === "in_call");

  const hasIncomingCall = computed(() => callState.value?.status === "ringing");

  // ============================================
  // Call State
  // ============================================

  /**
   * Force-clear the local call state.
   * Used as an escape hatch when state gets stuck.
   */
  function clearCallState() {
    callState.value = null;
  }

  async function fetchCallState() {
    try {
      const response = await axiosClient.get<GetCallStateResponse>(
        endpoints.TWILIO.CALL_STATE.GET,
        { params: { dialer_type: "browser" } }
      );
      callState.value = response.data.call;
      isActiveDialer.value = response.data.is_active_dialer;
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  /**
   * Refresh call state TTL on the server.
   * Used for long calls to prevent state expiration.
   */
  async function refreshCallState(): Promise<boolean> {
    try {
      const response = await axiosClient.post<RefreshCallStateResponse>(
        endpoints.TWILIO.CALL_STATE.REFRESH
      );
      return response.data.success;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  /**
   * Start heartbeat for long calls.
   * Automatically refreshes call state TTL every 5 minutes.
   */
  function startHeartbeat() {
    if (heartbeatInterval) {
      return; // Already running
    }

    heartbeatInterval = setInterval(() => {
      if (callState.value) {
        refreshCallState();
      } else {
        stopHeartbeat();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat() {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  }

  // ============================================
  // Access Token
  // ============================================

  async function getAccessToken(): Promise<AccessTokenResponse> {
    const response = await axiosClient.post<AccessTokenResponse>(
      endpoints.TWILIO.ACCESS_TOKEN
    );
    twilioAccessToken.value = response.data.token;
    return response.data;
  }

  // ============================================
  // Call Commands
  // ============================================

  async function answerCall(
    dialerType: DialerType = "browser"
  ): Promise<AnswerCallResponse> {
    const response = await axiosClient.post<AnswerCallResponse>(
      endpoints.TWILIO.CALL_COMMANDS.ANSWER,
      { from_dialer_type: dialerType }
    );
    return response.data;
  }

  async function rejectCall(): Promise<AnswerCallResponse> {
    const response = await axiosClient.post<AnswerCallResponse>(
      endpoints.TWILIO.CALL_COMMANDS.REJECT
    );
    return response.data;
  }

  async function resetCall(): Promise<boolean> {
    try {
      const response = await axiosClient.post<{ success: boolean }>(
        endpoints.TWILIO.CALL_COMMANDS.RESET
      );
      return response.data.success;
    } catch (error) {
      if (!isAxiosError(error)) {
        Sentry.captureException(error);
      }
      return false;
    }
  }

  // ============================================
  // Browser Online Registration
  // ============================================

  /**
   * Register browser as online dialer.
   * Called when connecting to backend so the API knows this user can receive calls.
   */
  async function registerBrowser(): Promise<boolean> {
    try {
      const response = await axiosClient.post(
        endpoints.TWILIO.DIALER.REGISTER,
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );
      isActiveDialer.value = response.data.active ?? true;
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  /**
   * Unregister browser (going offline).
   * Called on disconnect or page unload.
   */
  async function unregisterBrowser() {
    try {
      await axiosClient.post(
        endpoints.TWILIO.DIALER.UNREGISTER,
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  /**
   * Refresh browser online status (heartbeat).
   * Prevents TTL expiration during long sessions.
   */
  async function refreshBrowserStatus() {
    try {
      const response = await axiosClient.post(
        endpoints.TWILIO.DIALER.REFRESH,
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );

      if (!response.data.success) {
        await registerBrowser();
        return;
      }

      // Re-sync active status in case a DialerActivationEvent was missed
      if (response.data.active !== undefined) {
        isActiveDialer.value = response.data.active;
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  function startRegistrationHeartbeat() {
    if (registrationHeartbeatInterval) {
      return; // Already running
    }

    registrationHeartbeatInterval = setInterval(() => {
      if (connectionState.value === "connected") {
        refreshBrowserStatus();
      }
    }, REGISTRATION_HEARTBEAT_MS);
  }

  function stopRegistrationHeartbeat() {
    if (registrationHeartbeatInterval) {
      clearInterval(registrationHeartbeatInterval);
      registrationHeartbeatInterval = null;
    }
  }

  /**
   * Set up beforeunload handler to unregister when the tab closes.
   * Uses sendBeacon for reliable delivery during page unload.
   */
  function setupUnloadHandler() {
    removeUnloadHandler();

    boundUnloadHandler = () => {
      // Use sendBeacon for reliable delivery during page unload.
      // sendBeacon sends cookies automatically so cookie-based auth works.
      // For JWT-only auth, the TTL expiration on the API side is the fallback.
      const baseURL = (
        axiosClient.defaults.baseURL || window.location.origin
      ).replace(/\/$/, "");
      const url = `${baseURL}${endpoints.TWILIO.DIALER.UNREGISTER}`;
      const data = JSON.stringify({ dialer_type: "browser" });
      navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
    };

    window.addEventListener("beforeunload", boundUnloadHandler);
  }

  function removeUnloadHandler() {
    if (boundUnloadHandler) {
      window.removeEventListener("beforeunload", boundUnloadHandler);
      boundUnloadHandler = null;
    }
  }

  /**
   * Register browser as online dialer and activate heartbeat + unload handler.
   * Returns true if registration succeeded.
   */
  async function activateBrowserDialer(): Promise<boolean> {
    const registered = await registerBrowser();
    if (!registered) {
      return false;
    }
    isRegisteredAsDialer.value = true;
    startRegistrationHeartbeat();
    setupUnloadHandler();
    return true;
  }

  // ============================================
  // WebSocket Subscriptions
  // ============================================

  async function subscribeToChannels(uid: number) {
    const usersChannelName = `users.${uid}`;
    usersChannel = windowWithEcho.Echo.private(usersChannelName);
    usersChannel.listen(
      ".call_state_changed",
      (event: CallStateChangedEvent) => {
        handleCallStateChanged(event);
      }
    );

    usersChannel.listen(
      ".dialer_activation",
      (event: DialerActivationEvent) => {
        handleDialerActivation(event);
      }
    );
  }

  function handleCallStateChanged(event: CallStateChangedEvent) {
    // Always track call state, even when the browser is not the active dialer.
    // This ensures watchers (e.g. useDialerConnection → App.vue) see the full
    // null → non-null → null lifecycle and can clean up dashboard UI state
    // when a call handled by the desktop app ends.
    callState.value = event.state;
  }

  function handleDialerActivation(event: DialerActivationEvent) {
    if (event.dialer_type !== "browser") {
      return;
    }

    // Higher-priority device explicitly unregistered — browser can take over
    if (event.reason === "unregistered") {
      if (!isRegisteredAsDialer.value) {
        activateBrowserDialer();
      }
      return;
    }

    isActiveDialer.value = event.active;

    if (!event.active && isRegisteredAsDialer.value) {
      unregisterBrowser();
      stopRegistrationHeartbeat();
      removeUnloadHandler();
      isRegisteredAsDialer.value = false;
    }
  }

  // ============================================
  // Focus-Based Sync
  // ============================================

  const handleVisibilityChange = async () => {
    if (document.visibilityState === "visible" && isConnected.value) {
      await fetchCallState();
    }
  };

  function setupFocusSync() {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  // ============================================
  // Connection
  // ============================================

  /**
   * Connect to backend - subscribe to WebSocket channels and register browser as online.
   */
  async function connect(newUserId: number) {
    if (connectionState.value === "connected" && userId === newUserId) {
      return;
    }

    connectionState.value = "connecting";
    userId = newUserId;

    try {
      await subscribeToChannels(newUserId);
      await activateBrowserDialer();

      // If desktop is online, registerBrowser() returned active: false.
      // Fully withdraw so we don't stay registered as a non-active dialer.
      if (!isActiveDialer.value && isRegisteredAsDialer.value) {
        unregisterBrowser();
        stopRegistrationHeartbeat();
        removeUnloadHandler();
        isRegisteredAsDialer.value = false;
      }

      await fetchCallState();

      connectionState.value = "connected";
      setupFocusSync();
    } catch (error) {
      Sentry.captureException(error);
      connectionState.value = "error";
      throw error;
    }
  }

  /**
   * Disconnect from backend.
   */
  function disconnect() {
    stopHeartbeat();
    stopRegistrationHeartbeat();

    if (isRegisteredAsDialer.value) {
      unregisterBrowser();
    }

    // Fully unsubscribe from channels (Echo.leave sends pusher:unsubscribe)
    if (usersChannel) {
      const usersChannelName = `users.${userId}`;
      windowWithEcho.Echo.leave(usersChannelName);
      usersChannel = null;
    }

    document.removeEventListener("visibilitychange", handleVisibilityChange);
    removeUnloadHandler();

    userId = null;
    connectionState.value = "disconnected";
    callState.value = null;
    isActiveDialer.value = true; // Reset to active (default when alone)
    isRegisteredAsDialer.value = false;
  }

  return {
    // State
    connectionState,
    callState,
    isActiveDialer,
    isRegisteredAsDialer,
    twilioAccessToken,
    // Getters
    activeCall,
    isConnected,
    isOnCall,
    hasIncomingCall,
    // Actions
    clearCallState,
    connect,
    disconnect,
    fetchCallState,
    refreshCallState,
    startHeartbeat,
    stopHeartbeat,
    getAccessToken,
    answerCall,
    rejectCall,
    resetCall,
    registerBrowser,
    unregisterBrowser,
    refreshBrowserStatus,
  };
});

// Re-export types
export * from "@/services/dialer-api/types";
